"use client";

import { useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";

const AUTH_REQUIRED_PREFIXES = ["/account", "/admin", "/settings"];
const PUBLIC_AUTH_PREFIXES = ["/auth/", "/oauth/"];
const COOKIE_CHECK_INTERVAL = 3000;
const EC_LOGGED_IN_COOKIE = "ec_logged_in";

function isAuthRequiredPage(pathname: string): boolean {
  for (const prefix of AUTH_REQUIRED_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return true;
    }
  }
  return false;
}

function isPublicAuthPage(pathname: string): boolean {
  if (pathname === "/auth") return true;
  for (const prefix of PUBLIC_AUTH_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      return true;
    }
  }
  return false;
}

function getLoggedInEpoch(): string | null {
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(EC_LOGGED_IN_COOKIE + "=")) {
      return trimmed.substring(EC_LOGGED_IN_COOKIE.length + 1) || null;
    }
  }
  return null;
}

function hasLoggedInCookie(): boolean {
  return getLoggedInEpoch() !== null;
}

function clearSessionCookies(): void {
  const cookieNames = ["ec_session", "__Secure-ec_session", EC_LOGGED_IN_COOKIE];
  const domains = [".econchangers.com"];

  const cookieDomainMeta = document.querySelector('meta[name="cookie-domain"]');
  if (cookieDomainMeta) {
    const metaDomain = cookieDomainMeta.getAttribute("content");
    if (metaDomain && !domains.includes(metaDomain)) {
      domains.push(metaDomain);
    }
  }

  for (const name of cookieNames) {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    for (const domain of domains) {
      document.cookie = `${name}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }
}

function handleSignedOut(routerRef: React.RefObject<ReturnType<typeof useRouter>>): void {
  clearSessionCookies();

  const currentPath = window.location.pathname;
  if (isAuthRequiredPage(currentPath)) {
    window.location.href = "/";
  } else {
    routerRef.current.refresh();
  }
}

async function verifySessionWithServer(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/session", {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.user !== null;
  } catch {
    return true;
  }
}

export default function SessionStatusListener() {
  const router = useRouter();
  const pathname = usePathname();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const verifyingRef = useRef(false);
  const routerRef = useRef(router);
  routerRef.current = router;

  const wasLoggedInRef = useRef(false);
  const knownEpochRef = useRef<string | null>(null);

  const isOnPublicAuthPage = isPublicAuthPage(pathname);

  useEffect(() => {
    if (isOnPublicAuthPage) {
      return;
    }

    const initialEpoch = getLoggedInEpoch();
    wasLoggedInRef.current = initialEpoch !== null;
    knownEpochRef.current = initialEpoch;

    function disconnect() {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    }

    async function verifyAndHandleSession() {
      if (verifyingRef.current) return;
      verifyingRef.current = true;
      try {
        const valid = await verifySessionWithServer();
        if (!valid) {
          disconnect();
          handleSignedOut(routerRef);
        }
      } finally {
        verifyingRef.current = false;
      }
    }

    function connect() {
      if (eventSourceRef.current) {
        return;
      }

      const es = new EventSource("/api/sse/session-status", {
        withCredentials: true,
      });
      eventSourceRef.current = es;

      es.addEventListener("session-invalidated", () => {
        disconnect();
        wasLoggedInRef.current = false;
        knownEpochRef.current = null;
        handleSignedOut(routerRef);
      });

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;

        if (!hasLoggedInCookie()) {
          return;
        }

        verifyAndHandleSession();

        reconnectTimeoutRef.current = setTimeout(() => {
          if (hasLoggedInCookie()) {
            connect();
          }
        }, 5000);
      };
    }

    function handleSessionChange() {
      const currentEpoch = getLoggedInEpoch();
      const isLoggedIn = currentEpoch !== null;
      const wasLoggedIn = wasLoggedInRef.current;

      if (isLoggedIn && !wasLoggedIn) {
        wasLoggedInRef.current = true;
        knownEpochRef.current = currentEpoch;
        connect();
        routerRef.current.refresh();
        return;
      }

      if (isLoggedIn && wasLoggedIn && currentEpoch !== knownEpochRef.current) {
        disconnect();
        knownEpochRef.current = currentEpoch;
        connect();
        routerRef.current.refresh();
        return;
      }

      if (!isLoggedIn && wasLoggedIn) {
        wasLoggedInRef.current = false;
        knownEpochRef.current = null;
        disconnect();
        handleSignedOut(routerRef);
        return;
      }

      if (isLoggedIn && document.visibilityState === "visible") {
        verifyAndHandleSession();
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        handleSessionChange();
      }
    }

    if (hasLoggedInCookie()) {
      connect();
      verifyAndHandleSession();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    pollIntervalRef.current = setInterval(() => {
      const currentEpoch = getLoggedInEpoch();
      const isLoggedIn = currentEpoch !== null;
      const isConnected = eventSourceRef.current !== null;
      const wasLoggedIn = wasLoggedInRef.current;

      if (isLoggedIn && !wasLoggedIn) {
        wasLoggedInRef.current = true;
        knownEpochRef.current = currentEpoch;
        connect();
        routerRef.current.refresh();
      } else if (isLoggedIn && wasLoggedIn && currentEpoch !== knownEpochRef.current) {
        disconnect();
        knownEpochRef.current = currentEpoch;
        connect();
        routerRef.current.refresh();
      } else if (isLoggedIn && !isConnected && !verifyingRef.current) {
        connect();
      } else if (!isLoggedIn && wasLoggedIn) {
        wasLoggedInRef.current = false;
        knownEpochRef.current = null;
        disconnect();
        handleSignedOut(routerRef);
      } else if (!isLoggedIn && isConnected) {
        knownEpochRef.current = null;
        disconnect();
      }
    }, COOKIE_CHECK_INTERVAL);

    return () => {
      disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isOnPublicAuthPage]);

  return null;
}
